import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { TTreeItemDataViewModel } from '../../services/models/CourseViewModels';
import useEditablePageTitle from './useEditablePageTitle';

interface ICoursePageItemTitleProps {
  className?: string;
  data: TTreeItemDataViewModel;
  coursePageID?: string;
  fontSize?: number;
  onFocus?: (e: any) => void;
  onEditModeChange?: (isEditing: boolean) => void;
}

export default observer(function CoursePageItemTitle({ data, className, coursePageID, fontSize = 14, onFocus, onEditModeChange }: ICoursePageItemTitleProps) {
  const minTitleWidth = 72;
  const theme = useTheme();
  const { editTitleMode, isTitleTruncated, titleMeasureRef, onTitleChange, updateTitle, handleOnKeyDown, handleOnClick, handleOnDoubleClick, handleTextFieldClick, handleOnFocus } =
    useEditablePageTitle({
      data,
      coursePageID,
      fontSize,
      onFocus,
    });

  const handlePointerCapture = (event: React.MouseEvent | React.PointerEvent | React.TouchEvent) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (onEditModeChange) {
      onEditModeChange(editTitleMode);
    }
  }, [editTitleMode, onEditModeChange]);

  return !editTitleMode ? (
    <div onClick={handleOnClick} onDoubleClick={handleOnDoubleClick} style={{ minWidth: minTitleWidth, minHeight: 20, width: '100%', overflow: 'hidden', cursor: 'text' }}>
      <Tooltip
        title={data.title?.value ?? ''}
        disableHoverListener={!isTitleTruncated}
        disableFocusListener={!isTitleTruncated}
        disableTouchListener={!isTitleTruncated}
        arrow
      >
        <Typography
          ref={titleMeasureRef}
          component='span'
          variant='body2'
          fontSize={fontSize}
          className={className}
          noWrap
          style={{ textAlign: 'left', maxWidth: '100%', minWidth: 0, display: 'block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {data.title?.value}
        </Typography>
      </Tooltip>
    </div>
  ) : (
    <TextField
      data-pagetour='2'
      variant='standard'
      size='small'
      inputProps={{
        draggable: false,
        style: {
          fontSize: fontSize,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      }}
      hiddenLabel={true}
      defaultValue={data.title?.value}
      onClick={handleTextFieldClick}
      onDoubleClick={handleTextFieldClick}
      onDragStart={(e) => e.stopPropagation()}
      onMouseDownCapture={handlePointerCapture}
      onPointerDownCapture={handlePointerCapture}
      onTouchStartCapture={handlePointerCapture}
      onFocusCapture={handleOnFocus}
      onChange={(e: any) => onTitleChange(e.target?.value)}
      onKeyDown={handleOnKeyDown}
      onBlur={updateTitle}
      fullWidth
      autoFocus
      className={className}
      onFocus={(event) => {
        event.target.select();
      }}
      sx={{
        minWidth: 0,
        width: '100%',
        '& .MuiInputBase-root': {
          minWidth: 0,
          width: '100%',
        },
        '& .MuiInputBase-input': {
          minWidth: minTitleWidth,
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '& .MuiInputBase-input.MuiInputBase-input': {
          color: theme.palette.text.primary,
        },
      }}
    />
  );
});
